/**
 * Utility to automatically hide the live chat icon when modals/drawers are open.
 * This prevents the chat icon from appearing on top of modal popups.
 *
 * Usage: Call `initLiveChatVisibilityManager()` once when the app initializes.
 * The utility uses a MutationObserver to detect when modal/drawer elements are added to the DOM
 * and automatically hides/shows the chat icon accordingly.
 */

const CHAT_ICON_SELECTORS = ['async-messaging-custom-chat-icon-holder'];

// Selectors to detect modal/drawer overlays
const MODAL_SELECTORS = [
  '[data-radix-portal]', // Radix UI portals (used by @uhg-abyss/web/ui/Drawer, Modal, etc.)
  '.abyss-modal-content-container',
  '.abyss-modal-overlay',
  '[role="dialog"]',
  '[aria-modal="true"]',
  '[data-radix-popper-content-wrapper]',
];

/**
 * Checks if a dialog element is actually visible/active
 * This handles cases where role="dialog" exists but modal isn't truly open
 */
const isDialogVisible = (element: Element): boolean => {
  // If aria-hidden="true", dialog is not visible
  if (element.getAttribute('aria-hidden') === 'true') {
    return false;
  }

  // Check if element is actually visible via CSS
  if (element instanceof HTMLElement) {
    try {
      const styles = window.getComputedStyle(element);
      if (
        styles.display === 'none' ||
        styles.visibility === 'hidden' ||
        styles.opacity === '0'
      ) {
        return false;
      }
    } catch {
      return false;
    }
  }

  return true;
};

/**
 * Creates an encapsulated visibility manager instance.
 * This pattern ensures that state is isolated and won't conflict if used in multiple places.
 */
const createVisibilityManager = () => {
  let observer: MutationObserver | null = null;
  let isInitialized = false;

  /**
   * Checks if any modal/drawer is currently open and visible in the DOM
   */
  const isModalOpen = (): boolean =>
    MODAL_SELECTORS.some((selector) => {
      const elements = document.querySelectorAll(selector);
      return Array.from(elements).some((element) => {
        // For role="dialog" elements, verify they're actually visible
        if (selector === '[role="dialog"]') {
          return isDialogVisible(element);
        }

        return true;
      });
    });

  /**
   * Gets all chat icon elements
   */
  const getChatIconElements = (): HTMLElement[] => {
    const elements: HTMLElement[] = [];
    CHAT_ICON_SELECTORS.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        elements.push(element);
      }
    });
    return elements;
  };

  /**
   * Updates the visibility of the chat icon based on modal state
   */
  const updateChatIconVisibility = (): void => {
    const chatIcons = getChatIconElements();
    const shouldHide = isModalOpen();

    chatIcons.forEach((icon) => {
      if (shouldHide) {
        icon.style.display = 'none';
      } else {
        icon.style.display = '';
      }
    });
  };

  /**
   * Initializes the live chat visibility manager.
   * This sets up a MutationObserver that watches for DOM changes
   * and automatically hides/shows the chat icon when modals open/close.
   *
   * @returns A cleanup function to disconnect the observer
   */
  const initLiveChatVisibilityManager = (): (() => void) => {
    // Prevent multiple initializations
    if (isInitialized || typeof window === 'undefined') {
      return () => {};
    }

    isInitialized = true;

    // Create a MutationObserver to watch for DOM changes
    observer = new MutationObserver((mutations) => {
      const isModalRelatedNode = (node: Node): boolean => {
        if (!(node instanceof HTMLElement)) {
          return false;
        }

        return (
          MODAL_SELECTORS.some(
            (selector) =>
              node.matches?.(selector) || node.querySelector?.(selector)
          ) || node.hasAttribute?.('data-radix-portal')
        );
      };

      const hasModalRelatedNodes = (nodes: NodeList): boolean =>
        Array.from(nodes).some(isModalRelatedNode);

      // Check if any mutation involves modal-related elements
      const hasRelevantMutation = mutations.some((mutation) => {
        // Check for attribute changes on dialog elements (aria-hidden, aria-modal only)
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof HTMLElement
        ) {
          const target = mutation.target;
          // Only care about changes to dialog/modal elements, not all elements
          if (
            target.matches?.('[role="dialog"]') ||
            target.matches?.('[aria-modal]') ||
            target.matches?.('[aria-hidden]')
          ) {
            return true;
          }
        }

        if (mutation.addedNodes.length > 0) {
          return hasModalRelatedNodes(mutation.addedNodes);
        }

        if (mutation.removedNodes.length > 0) {
          return hasModalRelatedNodes(mutation.removedNodes);
        }

        return false;
      });

      if (hasRelevantMutation) {
        // Use requestAnimationFrame to batch updates and avoid layout thrashing
        // Add a small delay to ensure DOM has fully updated
        requestAnimationFrame(() => {
          // Delay to account for modal closing animations
          setTimeout(() => {
            updateChatIconVisibility();
          }, 400);
        });
      }
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true, // Watch for attribute changes (aria-hidden, aria-modal)
      attributeFilter: ['aria-hidden', 'aria-modal'],
      attributeOldValue: false, // Don't store old values to save memory
    });

    // Initial check in case modals are already open
    updateChatIconVisibility();

    // Return cleanup function
    return () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      isInitialized = false;

      // Restore chat icon visibility on cleanup
      const chatIcons = getChatIconElements();
      chatIcons.forEach((icon) => {
        icon.style.display = '';
      });
    };
  };

  /**
   * Manually trigger a visibility check.
   * Useful when you know a modal has opened/closed but the observer might not have caught it.
   */
  const checkLiveChatVisibility = (): void => {
    updateChatIconVisibility();
  };

  /**
   * Force hide the chat icon temporarily.
   * Returns a function to restore visibility.
   */
  const forceHideLiveChat = (): (() => void) => {
    const chatIcons = getChatIconElements();
    chatIcons.forEach((icon) => {
      icon.style.display = 'none';
    });

    return () => {
      // Only restore if no modals are open
      if (!isModalOpen()) {
        chatIcons.forEach((icon) => {
          icon.style.display = '';
        });
      }
    };
  };

  return {
    initLiveChatVisibilityManager,
    checkLiveChatVisibility,
    forceHideLiveChat,
  };
};

// Create singleton instance for default usage
const manager = createVisibilityManager();

export const initLiveChatVisibilityManager =
  manager.initLiveChatVisibilityManager;
export const checkLiveChatVisibility = manager.checkLiveChatVisibility;
export const forceHideLiveChat = manager.forceHideLiveChat;

export default initLiveChatVisibilityManager;
